import React, { useEffect, useState, useMemo } from "react";
import { Form, Input, Button, Card, Row, Col, message, Tag, Avatar } from "antd";
import { SearchOutlined, SendOutlined, MessageOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import colors from "../assets/styles/colour";
import dataService from "../networks/api/service/dataService";
import CustomTable from "../components/TableComponent/CustomTable";
import Logo from "../assets/images/bjp final.png";

const { TextArea } = Input;

const GuestNavbar = () => {
  const navigate = useNavigate();
  return (
    <div style={navStyles.header}>
      <div style={navStyles.inner}>
        {/* Back Button */}
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ color: colors.sidebarText, fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          style={navStyles.backBtn}
        />

        {/* Logo */}
        <img src={Logo} alt="Logo" style={navStyles.logo} />

        {/* App name + portal label */}
        <div style={navStyles.titleBlock}>
         
        </div>

        {/* Date + avatar on the right */}
        <div style={navStyles.right}>
          <div style={navStyles.dateBlock}>
            <span style={navStyles.dateLabel}>Today</span>
            <span style={navStyles.dateValue}>
              {new Date().toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
          <Avatar
            src="https://cdn-icons-png.flaticon.com/512/9542/9542536.png"
            style={navStyles.avatar}
          />
        </div>
      </div>
    </div>
  );
};

const navStyles = {
  header: {
    backgroundColor: colors.primary,
    height: 70,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
  inner: {
    width: "100%",
    padding: "0 24px",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 8,
    width: 36,
    height: 36,
    padding: 0,
    flexShrink: 0,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  logo: {
    height: "160px",
    width: "auto",
    objectFit: "contain",
    borderRadius: 8,
    marginTop: 10,
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
    flex: 1,
  },
  title: {
    color: colors.sidebarText,
    fontWeight: 800,
    fontSize: 17,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  subtitle: {
    color: colors.sidebarText || "rgba(255,255,255,0.65)",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginLeft: "auto",
  },
  dateBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    lineHeight: 1.3,
  },
  dateLabel: {
    color: colors.sidebarText || "rgba(255,255,255,0.55)",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  dateValue: {
    color: colors.sidebarText,
    fontSize: 12,
    fontWeight: 600,
  },
  avatar: {
    borderRadius: 10,
    height: 40,
    width: 40,
    border: "2px solid rgba(255,255,255,0.2)",
  },
};

const FeedbackManagement = () => {
  const [form] = Form.useForm();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isLoggedIn = !!localStorage.getItem("JWTtoken");

  useEffect(() => {
    if (isLoggedIn) fetchFeedbacks();
  }, [isLoggedIn]);

  const fetchFeedbacks = async () => {
    try {
      const response = await dataService.getAllFeedback();
      setFeedbacks(response?.data || []);
    } catch {
      message.error("Failed to fetch feedback");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        user: values.user,
        level: values.level,
        assignedTO: values.assignedTO,
        Description: values.description,
      };
      setLoading(true);
      await dataService.addFeedback(payload);
      message.success("Feedback submitted successfully!");
      form.resetFields();
    } catch {
      message.error("Failed to submit feedback");
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedbacks = useMemo(() => {
    if (!searchText.trim()) return feedbacks;
    const q = searchText.toLowerCase();
    return feedbacks.filter((item) =>
      ["user", "level", "assignedTO", "Description"].some((key) =>
        item[key]?.toString().toLowerCase().includes(q)
      )
    );
  }, [feedbacks, searchText]);

  const levelColors = { high: "#ff4d4f", medium: "#faad14", low: "#52c41a" };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      align: "center",
      render: (val) => <span style={{ fontWeight: 600, color: colors.textColor }}>{val}</span>,
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      align: "center",
      render: (val) => (
        <Tag
          color={levelColors[val?.toLowerCase()] || colors.background}
          style={{ fontWeight: 600, borderRadius: 20, padding: "2px 12px" }}
        >
          {val}
        </Tag>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTO",
      key: "assignedTO",
      align: "center",
      render: (val) => <span style={{ color: "#555", fontWeight: 500 }}>{val}</span>,
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      ellipsis: true,
      align: "center",
      render: (val) => <span style={{ color: "#777" }}>{val}</span>,
    },
  ];

  return (
    <>
      {!isLoggedIn && <GuestNavbar />}

      <div
        style={{
          padding: "28px 32px",
          paddingTop: isLoggedIn ? 28 : 88,
          minHeight: "100vh",
          backgroundColor: "#f5f6fa",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        }}
      >
        {/* Section Header */}
        <div style={pageStyles.sectionHeader}>
          <div style={pageStyles.headerLeft}>
            <div style={{ ...pageStyles.iconBadge, backgroundColor: colors.background }}>
              <MessageOutlined style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <h2 style={pageStyles.title}>Feedback Management</h2>
              <p style={pageStyles.subtitle}>
                {isLoggedIn
                  ? "Review all submitted feedback below"
                  : "We'd love to hear from you — fill in the form below"}
              </p>
            </div>
          </div>
          {isLoggedIn && (
            <div style={pageStyles.badge}>
              {filteredFeedbacks.length} record{filteredFeedbacks.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>

        {/* Guest: Feedback Form */}
        {!isLoggedIn && (
          <Card style={pageStyles.card} bodyStyle={{ padding: "28px 32px" }}>
            <div style={pageStyles.cardHeader}>
              <span style={pageStyles.cardTitle}>New Feedback</span>
              <div style={{ ...pageStyles.divider, backgroundColor: colors.background }} />
            </div>
            <Form layout="vertical" form={form}>
              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="user"
                    label={<span style={pageStyles.label}>User</span>}
                    rules={[{ required: true, message: "Please enter user" }]}
                  >
                    <Input placeholder="Enter user name" style={pageStyles.input} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="level"
                    label={<span style={pageStyles.label}>Level</span>}
                    rules={[{ required: true, message: "Please enter level" }]}
                  >
                    <Input placeholder="e.g. High / Medium / Low" style={pageStyles.input} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="assignedTO"
                    label={<span style={pageStyles.label}>Assigned To</span>}
                    rules={[{ required: true, message: "Please enter assigned person" }]}
                  >
                    <Input placeholder="Enter assigned person" style={pageStyles.input} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="description"
                    label={<span style={pageStyles.label}>Description</span>}
                    rules={[{ required: true, message: "Please enter description" }]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="Describe your feedback..."
                      style={{ ...pageStyles.input, resize: "none" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleSubmit}
                style={{
                  backgroundColor: colors.background,
                  border: "none",
                  borderRadius: 10,
                  height: 42,
                  paddingInline: 28,
                  fontWeight: 700,
                  fontSize: 14,
                  boxShadow: `0 4px 14px ${colors.background}55`,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Submit Feedback
              </Button>
            </Form>
          </Card>
        )}

        {/* Logged-in: Feedback Table */}
        {isLoggedIn && (
          <Card style={pageStyles.card} bodyStyle={{ padding: "24px 28px" }}>
            <div style={pageStyles.searchRow}>
              <Input
                prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                placeholder="Search by user, level, assigned to, description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{
                  borderRadius: 10,
                  border: "1.5px solid #e8e8e8",
                  padding: "8px 12px",
                  fontSize: 14,
                  maxWidth: 420,
                }}
              />
            </div>
            <CustomTable
              columns={columns}
              dataSource={filteredFeedbacks}
              rowKey={(record) => record._id}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: (
                  <div style={pageStyles.emptyState}>
                    <SearchOutlined style={{ fontSize: 32, color: "#ccc", marginBottom: 8 }} />
                    <p style={{ color: "#aaa", margin: 0 }}>No feedback matched your search</p>
                  </div>
                ),
              }}
            />
          </Card>
        )}
      </div>
    </>
  );
};

const pageStyles = {
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    color: "#1a1a2e",
    letterSpacing: "-0.3px",
    lineHeight: 1.2,
  },
  subtitle: {
    margin: 0,
    fontSize: 13,
    color: "#888",
    marginTop: 2,
  },
  badge: {
    backgroundColor: "#e8f0fe",
    color: "#3a6ee8",
    fontWeight: 700,
    fontSize: 13,
    padding: "6px 14px",
    borderRadius: 20,
  },
  card: {
    borderRadius: 16,
    border: "none",
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
    marginBottom: 24,
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1a1a2e",
    whiteSpace: "nowrap",
  },
  divider: {
    height: 2,
    width: "100%",
    borderRadius: 4,
    opacity: 0.25,
  },
  label: {
    fontWeight: 600,
    color: "#444",
    fontSize: 13,
  },
  input: {
    borderRadius: 10,
    border: "1.5px solid #e8e8e8",
    padding: "8px 12px",
    fontSize: 14,
  },
  searchRow: {
    marginBottom: 20,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px 0",
  },
};

export default FeedbackManagement;